<template>
    <modal size="xl" ref="modal" @close="$emit('close')" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <span v-if="involuntaryWithholdingLaw">Update: </span><span v-if="involuntaryWithholdingLaw" class="text-thin text-nowrap">{{ involuntaryWithholdingLaw.involuntary_withholding_type.name }}</span>
            <span v-if="!involuntaryWithholdingLaw">Add Involuntary Withholding Law</span>
        </template>
        <slot>

            <form class="mui-form" @submit.prevent="onSubmit">
                <div class="form-row mb-4">
                    <div class="col-6">
                        <form-input v-model="formData.starts_on" :errors="errors.starts_on" type="date" label="Starts On" />
                    </div>
                    <div class="col-6">
                        <form-input v-model="formData.inactive_on" :errors="errors.inactive_on" type="date" label="Expires On" />
                    </div>
                </div>

                <div class="form-row mb-2">
                    <div class="col-6">
                        <form-input v-model="formData.state_code" :errors="stateCodeErrors" type="select" label="Jurisdiction" :options="stateOptions" :readonly="!!involuntaryWithholdingLaw" />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col">
                        <form-input v-model="formData.involuntary_withholding_type" :errors="errors.involuntary_withholding_type" type="select" label="Type" :options="typeOptions" :readonly="!!involuntaryWithholdingLaw" />
                    </div>
                </div>

                <div v-if="formData.involuntary_withholding_type">
                    <div class="form-row mb-2" v-if="currentType.has_rate">
                        <div class="col-2">
                            <form-input v-model="formData.min_rate" :errors="errors.min_rate" type="integer" :label="currentType.has_min_max_rate ? 'Min. Rate' : 'Rate'" />
                        </div>
                        <div class="col-2" v-if="currentType.has_min_max_rate">
                            <form-input v-model="formData.max_rate" :errors="errors.max_rate" type="integer" label="Max. Rate" />
                        </div>
                        <div class="col-2" v-if="currentType.has_arrears_rate">
                            <form-input v-model="formData.arrears_rate" :errors="errors.arrears_rate" type="integer" label="Additional Arrears Rate" />
                        </div>
                        <div class="col-2" v-if="currentType.has_non_credit_rate">
                            <form-input v-model="formData.min_non_credit_rate" :errors="errors.min_non_credit_rate" type="integer" label="Non-credit Rate" />
                        </div>
                    </div>

                    <div class="mb-2" v-if="currentType.has_exempt_amount">
                        <div class="" v-for="filingStatus in filingStatuses">
                            <h6>Exempt Amounts for {{ filingStatus.name }}</h6>
                            <div class="form-row mb-2">
                                <div class="col">
                                    <form-input v-model="formData.exempt_amounts[filingStatus.id].base_amount" :errors="errors.exempt_amounts[filingStatus.id].base_amount" type="currency" label="Base Amount" />
                                </div>
                                <div class="col">
                                    <form-input v-model="formData.exempt_amounts[filingStatus.id].per_dependent_amount" :errors="errors.exempt_amounts[filingStatus.id].per_dependent_amount" type="currency" label="Per Dependent Amount" />
                                </div>
                                <div class="col">
                                    <form-input v-model="formData.exempt_amounts[filingStatus.id].additional_exempt_amount" :errors="errors.exempt_amounts[filingStatus.id].additional_exempt_amount" type="currency" label="Additional Exempt Amount" />
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </form>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row>
                <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Cancel</button>
                <button type="button" class="btn btn-block btn-primary" @click.prevent="onSubmit()">Save</button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import States from '@/states'
import FILING_STATUSES from './involuntaryWithholdingFilingStatuses'

export default {
    props: ['involuntaryWithholdingLaw', 'involuntaryWithholdingTypes'],
    mixins: [FormMixin, ModalMixin, ],
    computed: {
        stateCodeErrors() {
            return (this.errors.state_code || []).concat(this.errors.is_federal || [])
        },
        stateOptions() {
            const states = States.map(s => ({text: s.name, value: s.abbreviation}))
                .sort((a, b) => a.text.toLowerCase() > b.text.toLowerCase() ? 1 : -1)
            return [{text: 'Federal', value: '--'}, ...states]
        },
        typeOptions() {
            let types = this.involuntaryWithholdingTypes
            if (this.formData.state_code && this.formData.state_code != '--') {
                types = types.filter(w => {
                    return ['child-support', 'tax-levy', 'creditor-garnishment'].includes(w.type)
                })
            }

            const result = []
            types.forEach(w => {
                result.push({
                    text: w.name,
                    value: w.type,
                })
            })
            return result
        },
        currentType() {
            if (!this.formData.involuntary_withholding_type) {
                return null
            }

            return this.involuntaryWithholdingTypes.find(w => w.type == this.formData.involuntary_withholding_type)
        },
        filingStatuses() {
            return FILING_STATUSES
        },
    },
    watch: {
        'formData.involuntary_withholding_type': function() {
            this.$nextTick(() => {
                this.$bus.$emit('resize')
            })
        },
        'formData.state_code': function(n, o) {
            if (n == o) {return}
            this.formData.involuntary_withholding_type = ''
        },
    },
    data() {
        return {
            formRules: {
                starts_on: {presence: {allowEmpty: false}, date: true},
                state_code: {presence: {allowEmpty: false}},
                involuntary_withholding_type: {presence: {allowEmpty: false}},
            },
            formData: this.makeFormData(),
            errors: {exempt_amounts: {}},
        }
    },
    created() {
        this.resetExemptAmounts()
    },
    methods: {
        makeAmountExemptErrors() {
            const exemptAmountErrors = {}
            this.filingStatuses.forEach(x => {
                exemptAmountErrors[x.id] = []
            })
            return exemptAmountErrors
        },
        resetExemptAmounts() {
            const exemptAmounts = {}
            this.filingStatuses.forEach(x => {
                const existing = this.formData.exempt_amounts[x.id]
                const ea = this.involuntaryWithholdingLaw ? this.involuntaryWithholdingLaw.current_version.exempt_amounts.find(ea => ea.filing_status == x.id) : null
                exemptAmounts[x.id] = existing || {
                    base_amount: ea ? ea.base_amount : '',
                    per_dependent_amount: ea ? ea.per_dependent_amount : '',
                    additional_exempt_amount: ea ? ea.additional_exempt_amount : '',
                }
            })
            this.formData.exempt_amounts = exemptAmounts
            this.errors.exempt_amounts = this.makeAmountExemptErrors()
        },
        getFormRules(fields) {
            const rules = JSON.parse(JSON.stringify(this.formRules))

            if (this.formData.state_code == '--') {
                rules.state_code = {presence: {allowEmpty: true}}
            }

            if (this.currentType && this.currentType.has_non_credit_rate) {
                rules.min_non_credit_rate = {presence: {allowEmpty: false}}
                rules.max_non_credit_rate = {presence: {allowEmpty: false}}
            }

            return rules
        },
        getFormData() {
            const type = this.currentType
            const data = JSON.parse(JSON.stringify(this.formData))  // Make a copy

            if (type) {
                if (type.has_rate) {
                    if (!type.has_min_max_rate) {
                        data.max_rate = data.min_rate
                    }

                    if (!type.has_arrears_rate) {
                        data.arrears_rate = 0
                    }

                    if (type.has_non_credit_rate) {
                        data.max_non_credit_rate = data.min_non_credit_rate
                    }
                    else {
                        data.min_non_credit_rate = 0
                        data.max_non_credit_rate = 0
                    }
                }
                else {
                    data.min_rate = 100
                    data.max_rate = 100
                    data.arrears_rate = 0
                    data.min_non_credit_rate = 0
                    data.max_non_credit_rate = 0
                }

                data.involuntary_withholding_type = type.id
            }

            data.is_federal = data.state_code == '--'
            if (data.is_federal) {
                data.state_code = ''
            }

            return data
        },
        makeFormData() {
            if (this.involuntaryWithholdingLaw) {
                return {
                    starts_on: this.involuntaryWithholdingLaw.current_version.starts_on,
                    inactive_on: this.involuntaryWithholdingLaw.current_version.inactive_on,
                    state_code: this.involuntaryWithholdingLaw.is_federal ? '--' : this.involuntaryWithholdingLaw.state_code,
                    involuntary_withholding_type: this.involuntaryWithholdingLaw.involuntary_withholding_type.type,
                    min_rate: this.involuntaryWithholdingLaw.current_version.min_rate,
                    max_rate: this.involuntaryWithholdingLaw.current_version.max_rate,
                    arrears_rate: this.involuntaryWithholdingLaw.current_version.arrears_rate,
                    min_non_credit_rate: this.involuntaryWithholdingLaw.current_version.min_non_credit_rate,
                    max_non_credit_rate: this.involuntaryWithholdingLaw.current_version.max_non_credit_rate,
                    exempt_amounts: {},
                }
            } else {
                return {
                    starts_on: '',
                    inactive_on: '',
                    state_code: '',
                    involuntary_withholding_type: '',
                    min_rate: '',
                    max_rate: '',
                    arrears_rate: 0,
                    min_non_credit_rate: '',
                    max_non_credit_rate: '',
                    exempt_amounts: {},
                }
            }
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }
            this.errors.exempt_amounts = Object.assign(this.makeAmountExemptErrors(), this.errors.exempt_amounts || {})

            let promise, url;
            if (this.involuntaryWithholdingLaw) {
                url = `/admin/tax-engine/involuntary-withholding-laws/${this.involuntaryWithholdingLaw.id}`
                promise = this.$api.put(url, this.getFormData())
            }
            else {
                url = `/admin/tax-engine/involuntary-withholding-laws`
                promise = this.$api.post(url, this.getFormData())
            }

            this.$store.dispatch('START_LOADING')
            return promise.then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.errors.exempt_amounts = Object.assign(this.makeAmountExemptErrors(), this.errors.exempt_amounts || {})
                this.formErrors = errors.__all__
                this.onError()
            })
        },
    }
}
</script>
